import * as React from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download, Printer } from 'lucide-react';

import { api, ErroApi } from '@/lib/api';
import { cn, formatarData } from '@/lib/utils';
import { useAuth } from '@/features/auth/contexto';
import type { Especialidade, UbsResumida } from '@/features/admin/tipos';
import { descreverFiltros, lerFiltros, paraConsulta } from '@/features/relatorios/filtros';
import {
  BLOCOS,
  DESCRICOES_BLOCO,
  ROTULOS_BLOCO,
  ehBloco,
  type Bloco,
} from '@/features/relatorios/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { FiltrosRelatorio } from '@/components/relatorios/filtros-relatorio';
import { BlocoVolume } from '@/components/relatorios/bloco-volume';
import { BlocoQualidade } from '@/components/relatorios/bloco-qualidade';
import { BlocoDemanda } from '@/components/relatorios/bloco-demanda';
import { BlocoTempos } from '@/components/relatorios/bloco-tempos';
import { Botao } from '@/components/ui/button';

/**
 * Tela de relatórios — Gestor e Administrador.
 *
 * Um bloco por vez, e não tudo empilhado: quatro blocos numa página só dariam
 * quatro consultas pesadas a cada mudança de filtro, e uma rolagem em que nada
 * é encontrado. Os filtros valem para todos, e trocar de aba mantém o recorte.
 */
export function PaginaRelatorios() {
  const { usuario } = useAuth();
  const [parametrosUrl, setParametrosUrl] = useSearchParams();

  const parametroBloco = parametrosUrl.get('bloco');
  const bloco: Bloco = ehBloco(parametroBloco) ? parametroBloco : 'volume';
  const filtros = lerFiltros(parametrosUrl);
  const consulta = paraConsulta(filtros).toString();

  const atualizar = React.useCallback(
    (chave: string, valor: string) => {
      setParametrosUrl((atuais) => {
        const novos = new URLSearchParams(atuais);
        if (valor) novos.set(chave, valor);
        else novos.delete(chave);
        return novos;
      });
    },
    [setParametrosUrl],
  );

  const definirPeriodo = React.useCallback(
    (de: string, ate: string) => {
      setParametrosUrl((atuais) => {
        const novos = new URLSearchParams(atuais);
        novos.set('de', de);
        novos.set('ate', ate);
        return novos;
      });
    },
    [setParametrosUrl],
  );

  // Limpar preserva período e aba: o gestor quer tirar o recorte de unidade,
  // não voltar ao começo da navegação.
  const limpar = React.useCallback(() => {
    setParametrosUrl((atuais) => {
      const novos = new URLSearchParams();
      for (const chave of ['bloco', 'de', 'ate', 'limite']) {
        const valor = atuais.get(chave);
        if (valor) novos.set(chave, valor);
      }
      return novos;
    });
  }, [setParametrosUrl]);

  const { data: unidades } = useQuery({
    queryKey: ['ubs-ativas'],
    queryFn: () => api.get<UbsResumida[]>('/ubs/ativas'),
    staleTime: 5 * 60_000,
  });

  const { data: especialidades } = useQuery({
    queryKey: ['especialidades-ativas'],
    queryFn: () => api.get<Pick<Especialidade, 'id' | 'nome'>[]>('/especialidades/ativas'),
    staleTime: 5 * 60_000,
  });

  const [exportando, setExportando] = React.useState(false);

  const exportar = async () => {
    setExportando(true);
    try {
      const { blob, nome } = await api.getArquivo(`/relatorios/${bloco}/csv?${consulta}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nome ?? `kavi-${bloco}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (erro) {
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível exportar o relatório.',
      );
    } finally {
      setExportando(false);
    }
  };

  const nomes = {
    ubs: unidades?.find((unidade) => unidade.id === filtros.ubsId)?.nome,
    especialidade: especialidades?.find((item) => item.id === filtros.especialidadeId)?.nome,
  };

  return (
    <>
      <CabecalhoPagina
        titulo="Relatórios"
        descricao={
          usuario?.perfil === 'GESTOR'
            ? 'O percurso das solicitações nas unidades que você acompanha.'
            : 'O percurso das solicitações, da UBS até a decisão da Regulação.'
        }
        acoes={
          <div className="sem-impressao flex items-center gap-2">
            <Botao variante="contorno" onClick={() => window.print()}>
              <Printer aria-hidden="true" />
              {/* O rótulo inteiro não cabe em 360px ao lado do outro botão. */}
              <span className="hidden sm:inline">Imprimir ou salvar em PDF</span>
              <span className="sm:hidden">Imprimir</span>
            </Botao>
            <Botao variante="contorno" onClick={() => void exportar()} disabled={exportando}>
              <Download aria-hidden="true" />
              {exportando ? 'Gerando…' : 'Exportar CSV'}
            </Botao>
          </div>
        }
      />

      {/* Na tela, o recorte está nos controles logo abaixo. No papel, os
          controles somem — e uma folha de números sem procedência não serve
          de nada para quem a recebe. */}
      <div className="somente-impressao mb-4">
        <h2 className="text-lg font-semibold">
          {ROTULOS_BLOCO[bloco]} — {formatarData(filtros.de)} a {formatarData(filtros.ate)}
        </h2>
        <p className="text-silenciado-texto text-sm">
          {descreverFiltros(filtros, nomes).join(' · ')}
        </p>
        <p className="text-silenciado-texto text-xs">
          KAVI · emitido em {formatarData(new Date())} por {usuario?.nome}
        </p>
      </div>

      <FiltrosRelatorio
        filtros={filtros}
        unidades={unidades}
        especialidades={especialidades}
        aoMudar={atualizar}
        aoDefinirPeriodo={definirPeriodo}
        aoLimpar={limpar}
      />

      {/* As quatro abas não cabem lado a lado no celular: rolam na horizontal
          em vez de quebrar em duas linhas e empurrar o conteúdo para baixo. */}
      <div
        className="border-borda sem-impressao mb-5 flex gap-1 overflow-x-auto border-b sm:flex-wrap sm:overflow-visible"
        role="tablist"
        aria-label="Blocos do relatório"
      >
        {BLOCOS.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={item === bloco}
            aria-controls="painel-relatorio"
            title={DESCRICOES_BLOCO[item]}
            onClick={() => atualizar('bloco', item)}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
              'focus-visible:outline-foco focus-visible:outline-2 focus-visible:outline-offset-2',
              item === bloco
                ? 'border-marca text-marca'
                : 'text-silenciado-texto hover:text-fundo-texto border-transparent',
            )}
          >
            {ROTULOS_BLOCO[item]}
          </button>
        ))}
      </div>

      <div id="painel-relatorio" role="tabpanel" aria-label={ROTULOS_BLOCO[bloco]}>
        {bloco === 'volume' && <BlocoVolume consulta={consulta} />}
        {bloco === 'qualidade' && <BlocoQualidade consulta={consulta} />}
        {bloco === 'demanda' && <BlocoDemanda consulta={consulta} />}
        {bloco === 'tempos' && <BlocoTempos consulta={consulta} />}
      </div>
    </>
  );
}
